import React from 'react';
import PropTypes from 'prop-types';
import useStyles from '@front10/helpers/dist/UseStyles';
import { isSSR } from '@front10/utils';
import withCommonEMContainer from '../_commons/CommonEMContainer';

//-------------------------------------
// Basic components here
//-------------------------------------
import Box from '../../elements/Box';
import Row from '../../elements/Row';
import Col from '../../elements/Col';
import Disclaimer from '../../others/Disclaimer';
import MontlyHistogramCarousel from '../../controls/MontlyHistogramCarousel';
import DayCard from '../../items/DayCard';
import Legend from '../../others/Legend';
import Loading from '../../others/Loading';
import NoFaresBehavior from '../../others/NoFaresBehavior/NoFaresBehavior';
import SkeletonLightMonthlyHistogram from '../../others/Skeleton/SkeletonLightMonthlyHistogram';
import SkeletonMonthlyHistogram from '../../others/Skeleton/SkeletonMonthlyHistogram';
import NextDestinationAlert from '../../alerts/NextDestinationAlert';
import NoFeaturedPopularFaresAlert from '../../alerts/NoFeaturedPopularFaresAlert';
import ContainerErrorAlert from '../../alerts/ContainerErrorAlert';
import useFiltersDisableSettings from './../InteractiveMapContainer/filters/useFiltersDisabled';

//-------------------------------------
// EveryMundo integration
//-------------------------------------
import { getHistogramsByMonths } from '../../../_integrations/everymundo/histogram';
import { getAirports } from '../../../_integrations/everymundo/airports';

import { useFilters, OriginDestinationLogic } from '../_commons/filters';
import { useFareItemProps } from '../_commons/tracking';
import InlineFilters from '../../controls/InlineFilters/InlineFilters';
import { getResultsLength } from '../_commons/histogram';

import prePopulationDefaultProps from '../_commons/prepopulationDefaultProps/histogram';
import HeaderContainer from '../../elements/HeaderContainer';
import SecondaryFilters from '../../controls/SecondaryFilters';

function LightMontlyHistogramContainer(props) {
  const {
    getHistograms,
    getAirports: getAirportsFn,
    prePopulation,
    integrationSettings,
    meta,
    pageContext,
    maxItemsAmount,
    initialHistograms,
    origin,
    destination,
    switchOriginDestination,
    skeletonSettings,
    showLoading,
    showSkeletonScreen,
    disclaimer,
    fareItemOnClickAction,
    legend,
    loading,
    carousel,
    direction,
    showNoResultsPlaceholder,
    noResultsPlaceHolder,
    alert,
    showLegend,
    showFilters,
    showDisclaimer,
    disclaimerContentSettings,
    legendContentSettings,
    mainContentSettings,
    filterContentSettings,
    minInitialResults,
    theme,
    filters: { availableFilters, ...filters },
    skeletonType,
    originDestinationLogic,
    secondaryFilters,
    showSecondaryFilters = false,
    enableSEO = true
  } = props;

  const [className, style] = useStyles(props, { direction, theme });
  const myStyle = Object.assign({ minHeight: '400px' }, style);

  // Define filter props using deprecated container props (for backwards compatibility)
  // Don't rely on this props for new configurations, they will be removed in a future version
  const filtersConfig = {
    containerSettings: { justifyContent: 'center', ...filterContentSettings },
    originDestinationSettings: {
      components: {
        column: {
          lg: 10
        },
        origin,
        destination,
        switch: switchOriginDestination
      },
      showOrigin: true,
      showDestination: true,
      showSwitch: true
    },
    clearButtonDisplay: 'hidden',
    ...filters
  };

  const {
    fares: { match, ...fares },
    dispatch,
    tracking,
    retry,
    ...filtersState
  } = useFilters({
    originDestinationLogic,
    filter: {
      prePopulation,
      integrationSettings,
      meta,
      pageContext,
      getAirports: getAirportsFn,
      maxItemsAmount
    },
    initialData: initialHistograms ? { histograms: initialHistograms } : null,
    initialOrigins: undefined,
    getFares: getHistograms,
    faresTransformationLogic: 'histogram',
    minInitialResults,
    showNoResultsPlaceholder,
    availableFilters: showFilters ? availableFilters : [],
    showSecondaryFilters,
    availableSecondaryFilter: secondaryFilters ? secondaryFilters.availableFilters : [],
    hideBudgetOnMultiCurrency: filtersConfig.hideBudgetOnMultiCurrency,
    routeMode: true,
    getResultsLength,
    showOrigin:
      filtersConfig.originDestinationSettings && filtersConfig.originDestinationSettings.showOrigin,
    showDestination:
      filtersConfig.originDestinationSettings &&
      filtersConfig.originDestinationSettings.showDestination
  });

  const isOriginSelected = Boolean(filtersState.locations.origin.value);
  const isDestinationSelected = Boolean(filtersState.locations.destination.value);

  const secondaryFiltersDisableSettings = useFiltersDisableSettings(
    { ...filtersConfig, ...props.secondaryFilters },
    {
      budget: !isOriginSelected || !isDestinationSelected,
      flightType: !isOriginSelected || !isDestinationSelected,
      travelClass: !isOriginSelected || !isDestinationSelected,
      travelerInterest: !isOriginSelected || !isDestinationSelected,
      flightStopCount: !isOriginSelected || !isDestinationSelected
    }
  );

  const itemSettings = useFareItemProps(carousel.fareItem, dispatch, fareItemOnClickAction);

  const renderSecondaryFilters = showSecondaryFilters && (
    <SecondaryFilters
      {...filtersConfig}
      {...filtersState}
      {...props.secondaryFilters}
      {...secondaryFiltersDisableSettings}
      filter={{ ...{ prePopulation, integrationSettings, meta, getAirports } }}
    />
  );

  if (isSSR() && !Boolean(enableSEO)) {
    return null;
  }

  if (match('HIDDEN')) {
    return <NoFaresBehavior />;
  }

  function handleSeeMoreDeals() {
    tracking({ type: 'MORE_DEALS' });
    return true;
  }

  return (
    <Box
      position="relative"
      className={`${className} ${LightMontlyHistogramContainer.componentName}`}
      dataTestId="lightMontlyHistogramContainer"
      style={myStyle}
    >
      {showSkeletonScreen &&
        match('SKELETON') &&
        (skeletonType === 'SkeletonLightMonthlyHistogram' ? (
          <SkeletonLightMonthlyHistogram {...skeletonSettings} position="absolute" width="100" />
        ) : (
          <SkeletonMonthlyHistogram {...skeletonSettings} position="absolute" width="100" />
        ))}
      {showLoading && match('LOADING') && <Loading {...loading} />}
      <HeaderContainer
        defaultTitleValue={'Lowest Monthly Fares'}
        handleSeeMoreDeals={handleSeeMoreDeals}
        {...props}
      />
      {showFilters && <InlineFilters {...filtersConfig} {...filtersState} />}
      {renderSecondaryFilters}
      <Row gutters {...mainContentSettings} style={{ position: 'relative' }}>
        {match('ERROR') && (
          <Col>
            <ContainerErrorAlert {...alert} tryAgain={retry} />
          </Col>
        )}
        {match('WAITING_FOR_DESTINATION') && (
          <Col>
            <NextDestinationAlert {...noResultsPlaceHolder} />
          </Col>
        )}
        {match('NO_RESULTS') && (
          <Col>
            <NoFeaturedPopularFaresAlert {...noResultsPlaceHolder} />
          </Col>
        )}
        {match('COMPLETE') && (
          <>
            <Col>
              <MontlyHistogramCarousel
                {...carousel}
                fareItem={itemSettings}
                FareComponentInstance={DayCard}
                data={fares.data.histograms}
              />
            </Col>
            {showLegend && (
              <Col md="12" {...legendContentSettings}>
                <Legend {...legend} />
              </Col>
            )}
          </>
        )}
      </Row>
      {showDisclaimer && (
        <Row gutters {...disclaimerContentSettings}>
          <Col>
            <Disclaimer labelKey="cmp-light-monthly-container-disclaimer" {...disclaimer} />
          </Col>
        </Row>
      )}
    </Box>
  );
}

LightMontlyHistogramContainer.propTypes = {
  /**
   * Props of alerts
   */
  alert: PropTypes.objectOf(PropTypes.any),
  /**
   * Define if carousel navigation
   */
  carousel: PropTypes.objectOf(PropTypes.any),
  /**
   * To be used to set if t container is a controlled one
   */
  containerType: PropTypes.string,
  /**
   * Disclaimer Component
   */
  disclaimer: PropTypes.objectOf(PropTypes.any),
  /**
   * Destination Component
   */
  destination: PropTypes.objectOf(PropTypes.any),
  /**
   * Direction of the component rtl o ltr
   */
  direction: PropTypes.oneOf(['rtl', 'ltr']),
  /**
   * disclaimerContentSettings props
   */
  disclaimerContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Possible interactions will be Popup or Direct Deeplink
   */
  fareItemInteractionType: PropTypes.oneOf(['popup', 'deeplink']),
  /**
   * Action to be executed when clicking in fareItem
   */
  fareItemOnClickAction: PropTypes.func,
  /**
   * filterContentSettings props
   */
  filterContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Filters settings
   */
  filters: PropTypes.objectOf(PropTypes.any),
  /**
   * Function for request to obtain airports
   */
  getAirports: PropTypes.func,
  /**
   * Function for request to obtain histograms
   */
  getHistograms: PropTypes.func,
  /**
   * Hide the component if the ammount of initial results is lower than this value
   */
  minInitialResults: PropTypes.number,
  /**
   * Used to prepopulate the component
   */
  initialHistograms: PropTypes.arrayOf(PropTypes.any),
  /**
   * All integration configurations
   */
  integrationSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * legend Component props
   */
  legend: PropTypes.objectOf(PropTypes.any),
  /**
   * legend content settings
   */
  legendContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Loading Component
   */
  loading: PropTypes.objectOf(PropTypes.any),
  /**
   * mainContentSettings props
   */
  mainContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Number of month to show
   */
  maxItemsAmount: PropTypes.number,
  /**
   * Define if carousel navigation
   */
  navigation: PropTypes.bool,
  /**
   * Props of noResultsPlaceHolder
   */
  noResultsPlaceHolder: PropTypes.objectOf(PropTypes.any),
  /**
   * Origin Component
   */
  origin: PropTypes.objectOf(PropTypes.any),
  /**
   * Origin Component
   */
  pageContext: PropTypes.objectOf(PropTypes.any),
  /**
   * Component PrePopulation
   */
  prePopulation: PropTypes.objectOf(PropTypes.any),
  /**
   * Show Airport Iata Code in locations
   */
  showAirportIataCode: PropTypes.bool,
  /**
   * Determine if the component will show disclaimer
   */
  showDisclaimer: PropTypes.bool,
  /**
   * showFilters
   */
  showFilters: PropTypes.bool,
  /**
   * Determine if the component will show the skeleton screen while
   * loading (Default value will be false)
   */
  showSkeletonScreen: PropTypes.bool,
  /**
   * showLegend
   */
  showLegend: PropTypes.bool,
  /**
   * Shows Loading Component
   */
  showLoading: PropTypes.bool,
  /**
   * Define if show noResultsPlaceHolder
   */
  showNoResultsPlaceholder: PropTypes.bool,
  /**
   * Determine if the component will show the title
   */
  showTitle: PropTypes.bool,
  /**
   * SwitchOriginDestination Component
   */
  switchOriginDestination: PropTypes.objectOf(PropTypes.any),
  /**
   * SkeletonSettings Component
   */
  skeletonSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Title Component
   */
  title: PropTypes.objectOf(PropTypes.any),
  /**
   * Function to translate the component
   */
  translate: PropTypes.func,
  /**
   * Prop used for ajusts year on the fares.
   */
  uiDatesYearsAdjustmentPatch: PropTypes.number,
  /**
   * Number of month to visibles
   */
  visibleItemsAmount: PropTypes.number,
  /** LocalizationData via moment to be used as a date translator
   * https://github.com/moment/moment/blob/develop/src/locale/es.js
   */
  localizationData: PropTypes.objectOf(PropTypes.any),
  /**
   * Theme object on the component
   */
  theme: PropTypes.objectOf(PropTypes.any),
  /**
   * Parameters tracking
   */
  tracking: PropTypes.objectOf(PropTypes.any),
  /**
   * All meta configurations
   */
  meta: PropTypes.objectOf(PropTypes.any),
  /**
   * Type of skeleton to render
   */
  skeletonType: PropTypes.oneOf(['SkeletonLightMonthlyHistogram', 'SkeletonMonthlyHistogram']),
  /**
   * Login to load origin and destination airports
   * same: Origin and Destination are pre-populated or not
   *      a single airport service call is done to fill out the Origin
   *      and Destinations filters (for performance purposes).
   * different: Origin and Destination are pre-populated or not
   *      2 airports service calls are done to fill out the Origin
   *      and Destinations filters.
   */
  originDestinationLogic: PropTypes.oneOf([
    OriginDestinationLogic.SAME,
    OriginDestinationLogic.DIFFERENT
  ]),
  /**
   * Show the SubTitle Component
   */
  showSubTitle: PropTypes.bool,
  /**
   * subHeaderContentSettings props
   */
  subHeaderContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * titleKey of the component
   */
  subTitleKey: PropTypes.string,
  /**
   * SubTitle Component
   */
  subTitleSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Show Secondary Filters
   */
  showSecondaryFilters: PropTypes.bool,
  /**
   * Secondary Filters settings
   */
  secondaryFilters: PropTypes.objectOf(PropTypes.any)
};

LightMontlyHistogramContainer.defaultProps = {
  showSecondaryFilters: false,
  secondaryFilters: {},
  alert: {
    bgColor: 'secondary-4',
    textAlign: 'center',
    padding: '3',
    rounded: '@theme:card.rounded'
  },
  carousel: {},
  containerType: 'stateful',
  disclaimer: {
    fontSize: '3'
  },
  destination: {},
  direction: 'ltr',
  disclaimerContentSettings: {
    marginTop: '2'
  },
  fareItemInteractionType: 'popup', // direct deeplink
  fareItemOnClickAction: null,
  filterContentSettings: {
    marginTop: '4'
  },
  filters: {
    availableFilters: ['OriginDestination']
  },
  getAirports,
  getHistograms: getHistogramsByMonths,
  minInitialResults: null,
  initialHistograms: null,
  integrationSettings: {},
  legend: {},
  legendContentSettings: {
    marginTop: '2'
  },
  loading: {},
  mainContentSettings: {
    marginTop: '3'
  },
  maxItemsAmount: 12,
  navigation: true,
  noResultsPlaceHolder: {
    bgColor: 'light'
  },
  origin: {},
  // _SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED :)
  pageContext: {
    deviceCategory: 'DESKTOP',
    siteEdition: 'en_HK'
  },
  prePopulation: {
    ...prePopulationDefaultProps,
    interval: '1m'
  },
  showAirportIataCode: true,
  showDisclaimer: true,
  showFilters: true,
  showSkeletonScreen: true,
  showLegend: true,
  showLoading: true,
  showNoResultsPlaceholder: false,
  showTitle: true,
  switchOriginDestination: {
    marginWidth: '1',
    borderColor: 'white',
    color: 'none',
    bgColor: 'transparent',
    style: { lineHeight: '0' },
    iconSettings: {
      animation: 'mirror',
      size: 20,
      style: {
        stroke: '#212529',
        strokeWidth: 50,
        width: 23
      }
    }
  },
  skeletonSettings: {
    style: { backgroundColor: 'white' }
  },
  title: {
    content: ''
  },
  translate: () => {},
  uiDatesYearsAdjustmentPatch: null,
  visibleItemsAmount: 6,
  localizationData: null,
  tracking: {
    flexibleDates: false,
    passengerCount: 1,
    promoCode: 'EVERYMUNDO',
    eventType: 'fareListLightMontlyHistogram'
  },
  theme: null,
  meta: null,
  skeletonType: 'SkeletonLightMonthlyHistogram',
  originDestinationLogic: OriginDestinationLogic.SAME,
  showSubTitle: false,
  subHeaderContentSettings: {},
  subTitleKey: 'cmp-light-monthly-histogram-sub-title',
  subTitleSettings: {}
};
LightMontlyHistogramContainer.componentName = 'LightMontlyHistogramContainer';

// HOC with common functionality to all containers
export default withCommonEMContainer(LightMontlyHistogramContainer);
